/**
 * SectionCard
 *
 * Orchestrator component for a single V2 survey section card.
 *
 * Layout:
 *  1. SectionHeader title BAR — gray collapsed, blue expanded; section name + …menu
 *  2. Trigger subtitle — "Show section based on answer: …" when `visibleWhen` is set
 *  3. When expanded: inner content card with left-border state (blue/green/red) containing:
 *     - Section title Input + description TextArea (`register`-bound to RHF)
 *     - SectionTriggers (+ Add Section Trigger affordance + trigger rows)
 *     - SectionQuestions (question list; each question keeps its own V1 left-border state)
 *
 * Per-section left-border state encodes:
 *  - Red   (destructive)  — hasError
 *  - Green (successGreen) — hasBeenSaved && !isDirty
 *  - Blue  (brandPrimary) — !hasBeenSaved || isDirty  (default / new)
 */
import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { Input } from '../../../../../../components-v2/Input';
import { TextArea } from '../../../../../../components-v2/TextArea';
import type { SurveyPart, SurveySection } from '../types';
import { validateSection } from '../utils/validation';
import { SectionHeader } from './components/SectionHeader';
import { SectionQuestions } from './components/SectionQuestions';
import { SectionTriggers } from './components/SectionTriggers';
import { styleSheet } from './styles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SectionCardProps {
	/** The section's current form-state value (used for validation + display). */
	section: SurveySection;
	/** Zero-based index within the surveyParts array. */
	index: number;
	/** react-hook-form field-name prefix, e.g. "surveyForm.surveyParts.0". */
	fieldNamePrefix: string;
	/** Whether this card is currently expanded. */
	isExpanded: boolean;
	/** Expand the card. */
	onExpand: () => void;
	/** Duplicate this section (with all its questions). */
	onDuplicate: () => void;
	/** Delete this section. */
	onDelete: () => void;
	/**
	 * Whether the Delete action should be available in the … menu.
	 * False when this is the only remaining section — a V2 survey must keep ≥1 section
	 * so it cannot collapse back to the V1 editor.
	 */
	canDelete: boolean;
	/** Optional reorder affordance (surfaced in the … menu). */
	onReorder?: () => void;
	/** Whether the parent survey is being edited vs. created for the first time. */
	isEdit: boolean;
	/** Full ordered parts list — required by SectionTriggers and validateSection. */
	allSurveyParts: SurveyPart[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Return true if any leaf of a dirtyFields subtree is `true`. */
function hasDirtyLeaf(node: unknown): boolean {
	if (node === true) return true;
	if (node && typeof node === 'object') {
		return Object.values(node).some(hasDirtyLeaf);
	}
	return false;
}

/**
 * Return true if any leaf of an RHF `errors` subtree contains an error object
 * (a node with a string `message` property). RHF's errors objects look like
 * `{ message, type, ref }`, NOT like `dirtyFields` whose leaves are bare `true`.
 * `hasDirtyLeaf` only detects the latter, so we use this separate helper for
 * errors aggregation.
 */
function hasErrorLeaf(node: unknown): boolean {
	if (!node || typeof node !== 'object') return false;
	if (typeof (node as { message?: unknown }).message === 'string') return true;
	return Object.values(node).some(hasErrorLeaf);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SectionCard: React.FC<SectionCardProps> = ({
	section,
	index,
	fieldNamePrefix,
	isExpanded,
	onExpand,
	onDuplicate,
	onDelete,
	canDelete,
	onReorder,
	isEdit: _isEdit,
	allSurveyParts,
}) => {
	const { formState, register } = useFormContext();
	const { dirtyFields, errors } = formState;

	// -------------------------------------------------------------------------
	// Section IDs currently persisted to the BE. Derived from
	// `formState.defaultValues` (NOT `getValues`) so the set tracks the
	// BE-persisted snapshot, not the in-memory shape. This matters for the
	// V1→V2 upgrade: clicking `+ Add Section` writes fresh-UUID V2 sections
	// into `surveyForm.surveyParts` immediately, BEFORE SectionCard mounts —
	// a mount-time `getValues` would capture those fresh UUIDs and falsely
	// flag the new sections as `hasBeenSaved`, painting the header green
	// instead of blue. `formState.defaultValues` updates via `reset()` on
	// load and on save success, so it correctly reflects what the BE has.
	// Gated on `isEdit`: in create mode the seed section's UUID lives in
	// defaultValues but is NOT saved yet.
	// -------------------------------------------------------------------------
	const savedSectionIds = React.useMemo(() => {
		if (!_isEdit) return new Set<string>();
		const parts = (formState.defaultValues as { surveyForm?: { surveyParts?: Array<{ id?: string }> } } | undefined)
			?.surveyForm?.surveyParts;
		if (!parts) return new Set<string>();
		return new Set(parts.map(p => p?.id).filter((id): id is string => !!id));
	}, [_isEdit, formState.defaultValues]);

	// -------------------------------------------------------------------------
	// Derived state flags
	// -------------------------------------------------------------------------

	const sectionDirtyNode = (
		dirtyFields as Record<string, unknown> & {
			surveyForm?: { surveyParts?: unknown[] };
		}
	)?.surveyForm?.surveyParts?.[index];

	const sectionErrorNode = (
		errors as Record<string, unknown> & {
			surveyForm?: { surveyParts?: unknown[] };
		}
	)?.surveyForm?.surveyParts?.[index];

	const isDirty = hasDirtyLeaf(sectionDirtyNode);
	const hasBeenSaved = Boolean(section.id && savedSectionIds.has(section.id));
	const hasError = hasErrorLeaf(sectionErrorNode);

	// Section-level inline validation (catches EMPTY_SECTION etc.)
	const validationError = validateSection(section, allSurveyParts);

	// -------------------------------------------------------------------------
	// Border-color class selection — applied to the inner content card
	// -------------------------------------------------------------------------

	// Inner content card only houses title + description — its border state should
	// reflect errors on those fields, NOT errors nested under `questions[]` (those
	// drive per-question card borders + the section bar's aggregate hasError).
	const contentCardHasError =
		hasErrorLeaf((sectionErrorNode as { title?: unknown; description?: unknown } | undefined)?.title) ||
		hasErrorLeaf((sectionErrorNode as { title?: unknown; description?: unknown } | undefined)?.description);

	const innerBorderClass = contentCardHasError
		? styleSheet.contentError
		: hasBeenSaved && !isDirty
			? styleSheet.contentSaved
			: styleSheet.contentNew;

	// -------------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------------

	return (
		<div
			className={css(styleSheet.card)}
			data-testid={`section-${index}`}
			data-section-id={section.id}
			data-expanded={isExpanded}
			data-section-state={hasError ? 'error' : hasBeenSaved && !isDirty ? 'saved' : 'new'}
		>
			<SectionHeader
				section={section}
				index={index}
				isExpanded={isExpanded}
				isDirty={isDirty}
				hasBeenSaved={hasBeenSaved}
				hasError={hasError}
				onExpand={onExpand}
				onDuplicate={onDuplicate}
				onDelete={onDelete}
				canDelete={canDelete}
				onReorder={onReorder}
			/>

			{/*
			 * SectionTriggers sits OUTSIDE the expansion gate so the section's
			 * visibility rules (button to add, or existing trigger rows) remain
			 * visible whether the section is collapsed or expanded. Section 0
			 * still suppresses the button internally because nothing precedes it.
			 */}
			<SectionTriggers sectionIndex={index} fieldNamePrefix={fieldNamePrefix} allSurveyParts={allSurveyParts} />

			{validationError && <p className={css(styleSheet.validationHint)}>{validationError.message}</p>}

			{isExpanded && (
				<div className={css(styleSheet.expandedBody)}>
					<div className={css(styleSheet.contentCard, innerBorderClass)}>
						<label className={css(styleSheet.fieldLabel)}>Section Title</label>
						<Input
							placeholder='e.g. Basic Information'
							styles={[styleSheet.field]}
							{...register(`${fieldNamePrefix}.title`)}
						/>
						<label className={css(styleSheet.fieldLabel)}>Description</label>
						<TextArea
							placeholder='Optional description shown to respondents'
							styles={[styleSheet.field]}
							{...register(`${fieldNamePrefix}.description`)}
						/>
					</div>

					<SectionQuestions
						fieldNamePrefix={fieldNamePrefix}
						sectionIndex={index}
						isEdit={_isEdit}
						allSurveyParts={allSurveyParts}
					/>
				</div>
			)}
		</div>
	);
};

SectionCard.displayName = 'SectionCard';
